import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useRouteContext, useRouter } from '@tanstack/react-router';
import { Button, Flex, Form, Input, message, Typography } from 'antd';
import { useEffect } from 'react';

import { getGetAccountQueryKey, useUpdateAccountProfile } from '../../../../api';
import { AvatarReshuffle } from '../../../../modules/avatar/components/avatar-reshuffle';
import {
  UserProfileFormFields,
  UserProfileFormFieldValues,
} from '../../../../modules/user/components/user-profile-form';
import { SettingsLayoutContent } from '../../../../ui/components/layout';
import { useUnsavedChangesGuard } from '../../../../ui/hooks';

const SuperadminSettingsProfileRoute = () => {
  const queryClient = useQueryClient();
  const router = useRouter();

  const { account } = useRouteContext({ from: '/_authenticated' });

  const [form] = Form.useForm<UserProfileFormFieldValues & { avatarIndex?: number }>();
  Form.useWatch([], form);
  useUnsavedChangesGuard(() => form.isFieldsTouched());

  useEffect(() => {
    form.resetFields();
  }, [form, account]);

  const { mutate: updateProfile, isPending } = useUpdateAccountProfile({
    mutation: {
      onSuccess: () => {
        message.success('Saved');
        queryClient.resetQueries({ queryKey: getGetAccountQueryKey() });
        router.invalidate();
      },
      onError: () => {
        message.error('Failed to save');
      },
    },
  });

  const handleFinish = (values: UserProfileFormFieldValues & { avatarIndex?: number }) => {
    updateProfile({
      data: {
        firstName: values.firstName,
        lastName: values.lastName,
        phoneNumber: values.phoneNumber,
        workEmail: values.workEmail,
        ...(form.isFieldTouched('avatarIndex') ? { avatarIndex: values.avatarIndex } : {}),
      },
    });
  };

  return (
    <Form
      form={form}
      layout="vertical"
      validateTrigger="onSubmit"
      onFinish={handleFinish}
      initialValues={account}
    >
      <SettingsLayoutContent
        title="Profile"
        description="Manage your personal profile and account details."
        footer={
          <Form.Item shouldUpdate noStyle>
            {({ isFieldsTouched }) => {
              const isDirty = isFieldsTouched();
              return (
                <Flex justify={isDirty ? 'space-between' : 'flex-end'}>
                  {isDirty && (
                    <Button onClick={() => form.resetFields()} disabled={isPending}>
                      Reset
                    </Button>
                  )}
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={isPending}
                    disabled={!isDirty && !isPending}
                  >
                    Save
                  </Button>
                </Flex>
              );
            }}
          </Form.Item>
        }
      >
        <Flex vertical gap={8} style={{ marginBottom: 24 }}>
          <Typography.Text style={{ fontSize: 14, lineHeight: '22px' }}>
            User Avatar
          </Typography.Text>
          <Flex align="center" gap={16}>
            <Form.Item noStyle shouldUpdate={(prev, curr) => prev.avatarIndex !== curr.avatarIndex}>
              {({ getFieldValue }) => (
                <AvatarReshuffle
                  kind="user"
                  id={account.id}
                  currentIndex={getFieldValue('avatarIndex') ?? account.avatarIndex}
                  name={[account.firstName, account.lastName].filter(Boolean).join(' ')}
                  size={80}
                  onReshuffle={(nextIndex) => {
                    form.setFields([{ name: 'avatarIndex', value: nextIndex, touched: true }]);
                  }}
                  disabled={isPending}
                />
              )}
            </Form.Item>
            <Flex vertical>
              <Typography.Text style={{ fontWeight: 500, fontSize: 13 }}>
                Your Avatar
              </Typography.Text>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                Click the avatar to shuffle options.
              </Typography.Text>
            </Flex>
          </Flex>
        </Flex>
        <Form.Item name="avatarIndex" hidden>
          <Input />
        </Form.Item>
        <UserProfileFormFields showLabels />
      </SettingsLayoutContent>
    </Form>
  );
};

export const Route = createFileRoute('/_authenticated/superadmin/settings/profile')({
  component: SuperadminSettingsProfileRoute,
});
